/**
 * Badge — the CRM's status pill.
 *
 * One rounded-full recipe for the small labels that describe *state*: "Auto",
 * "Paused", "Needs reply", "Consent on file", channel and source tags. Every
 * one of these was previously spelled out inline, which is how three different
 * greens ended up on the same screen.
 *
 * DATA-COLOR CARVE-OUT (design system Rule 0): a hue that encodes domain data
 * is NOT chrome and does NOT migrate onto Badge. That means LifecycleBadge
 * (pipeline stage colors), MilestoneChip, eligibility-result colors, and
 * patient-status colors keep their own hues and their own components —
 * recruiters read those colors as meaning, and flattening them into this
 * seven-tone palette would destroy information. Badge is for chrome labels
 * whose color is merely a tone of voice.
 *
 * Sizes: `md` is the standard pill; `sm` is the denser one that rides inside a
 * table cell or next to a title. `dot` prepends a filled dot in the current
 * text color for pills that need a state marker beyond the word itself.
 */
import type { ReactNode } from 'react';

export type BadgeTone = 'gray' | 'primary' | 'green' | 'amber' | 'red' | 'purple' | 'cyan';

export interface BadgeProps {
  tone: BadgeTone;
  children: ReactNode;
  /** Leading state dot in the current text color. */
  dot?: boolean;
  /** `md` = standard pill (default); `sm` = the denser in-table pill. */
  size?: 'md' | 'sm';
  /** Appended last — margins/width only, by convention. */
  className?: string;
  /** Native tooltip, for pills whose label is abbreviated. */
  title?: string;
}

/** Written out in full: Tailwind cannot see classes assembled from fragments. */
const TONES: Record<BadgeTone, string> = {
  gray: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  primary: 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300',
  green: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  red: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  cyan: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300',
};

const SIZES = {
  md: 'px-2.5 py-0.5 text-xs',
  sm: 'px-2 py-0.5 text-[11px]',
} as const;

export default function Badge({
  tone,
  children,
  dot = false,
  size = 'md',
  className = '',
  title,
}: BadgeProps) {
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-medium ${SIZES[size]} ${TONES[tone]} ${className}`}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />}
      {children}
    </span>
  );
}
